import type { Action, SceneContext, SelfState, ShellView } from './types';
export const SPAWN = { x: 0, z: 8.3 };
export const ISLAND_RADIUS = 14;
export const PLATEAU_RADIUS = 10.8;
export const WATERLINE_RADIUS = 12;
export const WORLD_LIMIT = 22;
export const GRASS_RADIUS = 6.75;
export const TERRAIN_SIZE = 37;
export const MOVE_SPEED = 3.3;
export const COLLECT_RADIUS = 1.35;
export const REST_RADIUS = 1.7;
export const PICK_MS = 850;
export const RECONNECT_S = 30;
export type RestKind = 'towel' | 'sunbed';
export interface RestSpot { id: string; zoneId: string; kind: RestKind; x: number; z: number; rotY: number }
export interface RestZone { id: string; umbrella: { x: number; z: number }; table: { x: number; z: number; rotY: number }; spots: readonly RestSpot[] }
export const REST_HEIGHT: Record<RestKind, number> = { towel: .22, sunbed: .69 };
export const REST_ZONES: readonly RestZone[] = [
  { id: 'west-cove', umbrella: { x: -6.9, z: 3.8 }, table: { x: -8.9, z: 3.8, rotY: .25 }, spots: [
    { id: 'towel-pink', zoneId: 'west-cove', kind: 'towel', x: -5.1, z: 3.8, rotY: -.4 },
    { id: 'towel-blue', zoneId: 'west-cove', kind: 'sunbed', x: -7.6, z: 1.3, rotY: -.4 },
  ] },
  { id: 'north-cove', umbrella: { x: -3.4, z: -7.7 }, table: { x: -3.2, z: -9.7, rotY: -1.41 }, spots: [
    { id: 'north-towel', zoneId: 'north-cove', kind: 'towel', x: -3.6, z: -5.8, rotY: -2.06 },
    { id: 'north-sunbed', zoneId: 'north-cove', kind: 'sunbed', x: -.6, z: -8.2, rotY: -2.06 },
  ] },
  { id: 'east-cove', umbrella: { x: 7.5, z: -2.5 }, table: { x: 9.4, z: -2.2, rotY: -3.07 }, spots: [
    { id: 'east-towel', zoneId: 'east-cove', kind: 'towel', x: 5.7, z: -2.9, rotY: 2.57 },
    { id: 'east-sunbed', zoneId: 'east-cove', kind: 'sunbed', x: 7.7, z: 0, rotY: 2.57 },
  ] },
];
export const REST_SPOTS = REST_ZONES.flatMap(zone => zone.spots);
export function restSpotNear(x: number, z: number, tol = .6): RestSpot | undefined {
  return REST_SPOTS.find(spot => Math.hypot(spot.x - x, spot.z - z) <= tol);
}
export const PALMS: readonly (readonly [number, number, number])[] = [
  [6.6, -6.2, 1.15], [-6.4, -6.4, 1.05], [2.3, -7.6, .95],
  [9.5, 1.3, 1], [-10.2, -1.3, 1.1], [-1.3, -2.5, 1.05],
];
export const ROCKS: readonly (readonly [number, number])[] = [[10.8, -6.4], [-11.1, 6.4], [-8.3, -8.9]];
export const SHELL_CANDIDATES = [
  { x: .8, z: 7 }, { x: 3.2, z: 6.6 }, { x: 5.2, z: 4.8 },
  { x: 3.2, z: 2.4 }, { x: .4, z: 3.8 }, { x: -1.8, z: 6.5 },
  { x: 7.3, z: 7 }, { x: 9.7, z: 3.4 }, { x: 4.4, z: -.4 },
  { x: 0, z: -4.5 }, { x: -6, z: -9 }, { x: -9.5, z: -3.6 },
  { x: 11.6, z: .4 }, { x: 3.1, z: 11.2 }, { x: -5.2, z: 10.2 },
];
export function heightAt(x: number, z: number) {
  const r = Math.hypot(x, z);
  return r < PLATEAU_RADIUS ? 0.26 : Math.max(-0.65, 0.26 - (r - PLATEAU_RADIUS) * 0.25);
}
export function isSwimming(x: number, z: number) { return Math.hypot(x, z) > ISLAND_RADIUS; }
export function clampToWorld(x: number, z: number) {
  const r = Math.hypot(x, z), scale = r > WORLD_LIMIT ? WORLD_LIMIT / r : 1;
  return { x: x * scale, z: z * scale };
}
export function layoutShells(seed: number, cycle: number): ShellView[] {
  let state = (seed ^ Math.imul(cycle + 1, 0x9e3779b1)) >>> 0;
  const random = () => { state = (Math.imul(state, 1664525) + 1013904223) >>> 0; return state / 4294967296; };
  // First batch stays near the arrival beach; later batches explore the whole coast.
  const candidates = SHELL_CANDIDATES.slice(0, cycle === 0 ? 6 : undefined);
  for (let i = candidates.length - 1; i > 0; i--) { const j = Math.floor(random() * (i + 1)); [candidates[i], candidates[j]] = [candidates[j], candidates[i]]; }
  return candidates.slice(0, 5).map((p, i) => ({ ...p, id: `c${cycle}-${i}`, color: random() < 0.5 ? 'pink' : 'blue' }));
}
export function contextFor(x: number, z: number, action: Action, self: SelfState | null): SceneContext {
  if (action === 'lie') return { kind: 'standUp' };
  if (action === 'pick') return { kind: 'none' };
  const shell = self?.shells.filter(s => !self.picked.includes(s.id))
    .sort((a, b) => Math.hypot(a.x - x, a.z - z) - Math.hypot(b.x - x, b.z - z))
    .find(s => Math.hypot(s.x - x, s.z - z) <= COLLECT_RADIUS);
  if (shell) return { kind: 'collect', shellId: shell.id };
  const spot = restSpotNear(x, z, REST_RADIUS);
  return spot ? { kind: 'rest', spotId: spot.id } : { kind: 'none' };
}
